import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  useGetAllSyncStatusesAsync,
  useGetAllSyncSchedulesAsync,
  useUpdateSyncScheduleAsync,
  useTriggerSyncNowAsync,
} from '../../services/SyncStatusService/hooks/useMutation';
import {
  ISyncScheduleProps,
  ISyncStatusProps,
} from '../../services/SyncStatusService/types';
import { AppContext } from '../../context/AppContextProvider';
import HeaderDashboard from 'pages/DashboardPageClient/_components/_ui/Header';
import { useJobQuery } from '../../services/Jobs/hooks/useQuery';
import SyncActiveToast from '../../components/SyncActiveToast';
import ProgressDialog from '../../components/ProgressDialog';
import DeviceFacialSyncModal from './_components/DeviceFacialSyncModal';

const DETAIL_REFRESH_MS = 3000;

// bg/panel/panel2/text/muted/accent/border seguem o tema de navegação (var(--app-*)); good/warn/bad
// são cores semânticas de status (sucesso/atenção/erro) e ficam fixas independente do tema.
const COLORS = {
  bg: 'var(--app-bg)',
  panel: 'var(--app-surface-1)',
  panel2: 'var(--app-surface-2)',
  text: 'var(--app-text)',
  muted: 'var(--app-accent-muted)',
  accent: 'var(--app-accent)',
  good: '#34d399',
  warn: '#fbbf24',
  bad: '#f87171',
  border: 'var(--app-table-border)',
};

const STATUS_STYLES: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  Idle: { label: 'Aguardando', bg: COLORS.border, color: COLORS.muted },
  Running: {
    label: 'Em execução',
    bg: 'var(--app-accent-bg)',
    color: COLORS.accent,
  },
  Success: {
    label: 'Concluído',
    bg: 'rgba(52,211,153,.15)',
    color: COLORS.good,
  },
  Failed: { label: 'Falhou', bg: 'rgba(248,113,113,.15)', color: COLORS.bad },
};

const SERVICE_LABELS: Record<string, string> = {
  SponteSyncBackgroundService: 'Sponte',
  EscolaWebSyncBackgroundService: 'EscolaWeb (Catraca)',
  EscolarManagerSyncBackgroundService: 'Escolar Manager',
  TotvsIENHSyncBackgroundService: 'TOTVS IENH',
  SophiaSyncBackgroundService: 'Sophia',
  ActiveSoftSyncBackgroundService: 'ActiveSoft',
  PhidelisSyncBackgroundService: 'Phidelis',
  GvdasaCommboxSyncBackgroundService: 'GVDASA Commbox',
  FaceRegister: 'Reconhecimento Facial (Faces, Placas e ControlID)',
};

const getServiceLabel = (serviceName: string) =>
  SERVICE_LABELS[serviceName] || serviceName;

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
};

const REFRESH_OPTIONS = [
  { value: 5000, label: 'Atualizar: 5s' },
  { value: 15000, label: 'Atualizar: 15s' },
  { value: 30000, label: 'Atualizar: 30s' },
  { value: 60000, label: 'Atualizar: 60s' },
];

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.Idle;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 9px',
        borderRadius: 999,
        textTransform: 'uppercase',
        letterSpacing: '.03em',
        bgcolor: style.bg,
        color: style.color,
      }}
    >
      {style.label}
    </Box>
  );
}

function IntegrationCard({
  item,
  onOpenDetails,
}: {
  item: ISyncStatusProps;
  onOpenDetails: (serviceName: string) => void;
}) {
  return (
    <Box
      sx={{
        bgcolor: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '10px',
        padding: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: '10px',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: COLORS.text }}>
          {getServiceLabel(item.serviceName)}
        </Typography>
        <StatusBadge status={item.status} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          mb: '10px',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: item.failureCount > 0 ? COLORS.bad : COLORS.text,
            }}
          >
            {item.failureCount ?? 0}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
            Falhas
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: COLORS.good }}
          >
            {formatDate(item.lastSuccessUtc)}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
            Último sucesso
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          fontSize: 12,
          color: COLORS.muted,
          borderTop: `1px solid ${COLORS.border}`,
          pt: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <span>Última execução: {formatDate(item.lastRunUtc)}</span>
        <span>Próxima execução: {formatDate(item.nextRunUtc)}</span>
        {item.lastResultSummary && (
          <span style={{ color: COLORS.text }}>
            {item.lastResultSummary}
          </span>
        )}
      </Box>

      {item.lastErrorMessage && (
        <Box
          sx={{
            fontSize: 12,
            color: COLORS.bad,
            mt: '6px',
            wordBreak: 'break-word',
          }}
        >
          ⚠ {item.lastErrorMessage}
        </Box>
      )}

      <Button
        onClick={() => onOpenDetails(item.serviceName)}
        sx={{
          mt: '12px',
          width: '100%',
          bgcolor: COLORS.panel2,
          color: COLORS.text,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '6px',
          fontSize: 12,
          textTransform: 'none',
          '&:hover': { borderColor: COLORS.accent },
        }}
      >
        Ver detalhes
      </Button>
    </Box>
  );
}

function DetailStat({
  value,
  label,
  color,
}: {
  value: React.ReactNode;
  label: string;
  color?: string;
}) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        sx={{ fontSize: 18, fontWeight: 600, color: color || COLORS.text }}
      >
        {value}
      </Typography>
      <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
        {label}
      </Typography>
    </Box>
  );
}

function ScheduleEditor({
  schedule,
  onSave,
  isSaving,
}: {
  schedule: ISyncScheduleProps;
  onSave: (hour: number, minute: number) => void;
  isSaving: boolean;
}) {
  const [hour, setHour] = useState(schedule.hour);
  const [minute, setMinute] = useState(schedule.minute);

  useEffect(() => {
    setHour(schedule.hour);
    setMinute(schedule.minute);
  }, [schedule.hour, schedule.minute]);

  const dirty = hour !== schedule.hour || minute !== schedule.minute;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        mt: '10px',
      }}
    >
      <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
        Roda 1x/dia às:
      </Typography>
      <TextField
        size="small"
        type="number"
        value={hour}
        onChange={(e) =>
          setHour(Math.min(23, Math.max(0, Number(e.target.value) || 0)))
        }
        inputProps={{ min: 0, max: 23, style: { width: 40, color: COLORS.text } }}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
        }}
      />
      <Typography sx={{ color: COLORS.muted }}>:</Typography>
      <TextField
        size="small"
        type="number"
        value={minute}
        onChange={(e) =>
          setMinute(Math.min(59, Math.max(0, Number(e.target.value) || 0)))
        }
        inputProps={{ min: 0, max: 59, style: { width: 40, color: COLORS.text } }}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
        }}
      />
      <Button
        disabled={!dirty || isSaving}
        onClick={() => onSave(hour, minute)}
        sx={{
          bgcolor: COLORS.panel2,
          color: dirty ? COLORS.accent : COLORS.muted,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '6px',
          fontSize: 12,
          textTransform: 'none',
        }}
      >
        Salvar
      </Button>
    </Box>
  );
}

function ProgressDetailDialog({
  item,
  schedule,
  onClose,
  onSaveSchedule,
  isSavingSchedule,
  onTriggerNow,
  isTriggering,
}: {
  item: ISyncStatusProps | null;
  schedule: ISyncScheduleProps | null;
  onClose: () => void;
  onSaveSchedule: (serviceName: string, hour: number, minute: number) => void;
  isSavingSchedule: boolean;
  onTriggerNow: (serviceName: string) => void;
  isTriggering: boolean;
}) {
  const open = !!item;
  const total = item?.progressTotal ?? 0;
  const processed = item?.progressProcessed ?? 0;
  const hasProgress = !!item && total > 0;
  const pct = hasProgress ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: COLORS.panel,
          color: COLORS.text,
          border: `1px solid ${COLORS.border}`,
        },
      }}
    >
      {item && (
        <DialogContent sx={{ padding: '20px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: '16px',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                {getServiceLabel(item.serviceName)}
              </Typography>
              <Box sx={{ mt: '6px' }}>
                <StatusBadge status={item.status} />
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: COLORS.muted }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
              mb: '16px',
            }}
          >
            {schedule ? (
              <ScheduleEditor
                schedule={schedule}
                isSaving={isSavingSchedule}
                onSave={(hour, minute) =>
                  onSaveSchedule(item.serviceName, hour, minute)
                }
              />
            ) : (
              <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                Roda a cada minuto (horário fixo, não configurável).
              </Typography>
            )}
            <Button
              disabled={isTriggering || item.status === 'Running'}
              onClick={() => onTriggerNow(item.serviceName)}
              sx={{
                bgcolor: COLORS.panel2,
                color: COLORS.accent,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                fontSize: 12,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: COLORS.accent },
              }}
            >
              {item.status === 'Running' ? 'Já em execução…' : 'Sincronizar agora'}
            </Button>
          </Box>

          {item.status === 'Running' && (
            <Box sx={{ mb: '16px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: '6px',
                }}
              >
                <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                  {hasProgress
                    ? `Processando ${processed} de ${total}`
                    : 'Sincronizando…'}
                </Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                  {hasProgress ? `${pct}%` : ''}
                </Typography>
              </Box>
              <LinearProgress
                variant={hasProgress ? 'determinate' : 'indeterminate'}
                value={pct}
                sx={{
                  height: 8,
                  borderRadius: 6,
                  bgcolor: COLORS.panel2,
                  '& .MuiLinearProgress-bar': { bgcolor: COLORS.accent },
                }}
              />
              {item.progressCurrentItem && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: COLORS.muted,
                    mt: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  Processando agora: {item.progressCurrentItem}
                </Typography>
              )}
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              mb: '16px',
            }}
          >
            <DetailStat value={item.progressTotal ?? '—'} label="Total" />
            <DetailStat
              value={item.progressProcessed ?? '—'}
              label="Processados"
            />
            <DetailStat
              value={item.progressCreated ?? '—'}
              label="Criados"
              color={COLORS.good}
            />
            <DetailStat
              value={item.progressUpdated ?? '—'}
              label="Atualizados"
              color={COLORS.accent}
            />
            <DetailStat
              value={item.progressExisting ?? '—'}
              label="Existentes"
            />
            <DetailStat
              value={item.progressRemoved ?? '—'}
              label="Removidos"
              color={COLORS.warn}
            />
            <DetailStat
              value={item.progressErrors ?? item.failureCount ?? 0}
              label="Erros"
              color={COLORS.bad}
            />
          </Box>

          <Box
            sx={{
              fontSize: 12,
              color: COLORS.muted,
              borderTop: `1px solid ${COLORS.border}`,
              pt: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <span>Última execução: {formatDate(item.lastRunUtc)}</span>
            <span>Próxima execução: {formatDate(item.nextRunUtc)}</span>
            <span>Último sucesso: {formatDate(item.lastSuccessUtc)}</span>
            {item.lastResultSummary && (
              <span style={{ color: COLORS.text }}>
                Resumo: {item.lastResultSummary}
              </span>
            )}
          </Box>

          {item.lastErrorMessage && (
            <Box
              sx={{
                fontSize: 12,
                color: COLORS.bad,
                mt: '10px',
                wordBreak: 'break-word',
              }}
            >
              ⚠ {item.lastErrorMessage}
            </Box>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}

function SyncStatusPage() {
  const { showSnackbar } = useContext(AppContext);
  const [statuses, setStatuses] = useState<ISyncStatusProps[]>([]);
  const [schedules, setSchedules] = useState<ISyncScheduleProps[]>([]);
  const [refreshMs, setRefreshMs] = useState<number>(15000);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [detailsServiceName, setDetailsServiceName] = useState<string | null>(
    null,
  );
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const [isDeviceSyncModalOpen, setIsDeviceSyncModalOpen] = useState(false);
  const [deviceSyncJobId, setDeviceSyncJobId] = useState<number | null>(null);
  const [isDeviceSyncToastOpen, setIsDeviceSyncToastOpen] = useState(false);
  const [isDeviceSyncDialogOpen, setIsDeviceSyncDialogOpen] = useState(false);
  const { data: deviceSyncJob } = useJobQuery(deviceSyncJobId);

  const { mutateAsync: mutateGetAllSyncStatusesAsync, isLoading } =
    useGetAllSyncStatusesAsync();
  const { mutateAsync: mutateGetAllSyncSchedulesAsync } =
    useGetAllSyncSchedulesAsync();
  const { mutateAsync: mutateUpdateSyncScheduleAsync, isLoading: isSavingSchedule } =
    useUpdateSyncScheduleAsync();
  const { mutateAsync: mutateTriggerSyncNowAsync, isLoading: isTriggering } =
    useTriggerSyncNowAsync();

  const reqAllStatuses = () =>
    mutateGetAllSyncStatusesAsync()
      .then((data) => {
        setStatuses(data);
        setLastUpdatedAt(new Date());
      })
      .catch(() => {
        showSnackbar(
          'Não foi possível carregar o status das sincronizações.',
          'error',
        );
      });

  const reqAllSchedules = () =>
    mutateGetAllSyncSchedulesAsync()
      .then(setSchedules)
      .catch(() => {
        showSnackbar(
          'Não foi possível carregar os horários configurados.',
          'error',
        );
      });

  const handleSaveSchedule = (serviceName: string, hour: number, minute: number) =>
    mutateUpdateSyncScheduleAsync({ serviceName, hour, minute })
      .then(() => {
        showSnackbar('Horário atualizado.', 'success');
        reqAllSchedules();
      })
      .catch(() => {
        showSnackbar('Não foi possível atualizar o horário.', 'error');
      });

  const handleTriggerNow = (serviceName: string) =>
    mutateTriggerSyncNowAsync(serviceName)
      .then(() => {
        showSnackbar('Sincronização disparada.', 'success');
        setTimeout(reqAllStatuses, 1500);
      })
      .catch(() => {
        showSnackbar('Não foi possível disparar a sincronização.', 'error');
      });

  useEffect(() => {
    reqAllStatuses();
    reqAllSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detailsItem = useMemo(
    () => statuses.find((s) => s.serviceName === detailsServiceName) || null,
    [statuses, detailsServiceName],
  );

  const detailsSchedule = useMemo(
    () => schedules.find((s) => s.serviceName === detailsServiceName) || null,
    [schedules, detailsServiceName],
  );

  // Enquanto o modal de detalhes estiver aberto numa integração em execução,
  // atualiza bem mais rápido pra dar a sensação de progresso ao vivo.
  const effectiveRefreshMs =
    detailsItem && detailsItem.status === 'Running'
      ? DETAIL_REFRESH_MS
      : refreshMs;

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(reqAllStatuses, effectiveRefreshMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRefreshMs]);

  const sortedStatuses = [...statuses].sort((a, b) =>
    getServiceLabel(a.serviceName).localeCompare(getServiceLabel(b.serviceName)),
  );

  return (
    <>
      <HeaderDashboard></HeaderDashboard>
      <Box
        sx={{
          bgcolor: COLORS.bg,
          color: COLORS.text,
          padding: '24px',
          minHeight: '100%',
          fontFamily: '-apple-system, Segoe UI, Roboto, Arial, sans-serif',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: '12px',
            mb: '20px',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 600, mb: '4px' }}>
              Status das Sincronizações
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>
              Acompanhamento ao vivo das sincronizações automáticas das
              integrações e do reconhecimento facial (Faces, Placas e
              ControlID).
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
              {lastUpdatedAt
                ? `Atualizado em ${lastUpdatedAt.toLocaleTimeString('pt-BR')}`
                : '—'}
            </Typography>
            <Select
              size="small"
              value={refreshMs}
              onChange={(e) => setRefreshMs(Number(e.target.value))}
              sx={{
                bgcolor: COLORS.panel2,
                color: COLORS.text,
                fontSize: 13,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.border,
                },
                '& .MuiSvgIcon-root': { color: COLORS.text },
              }}
            >
              {REFRESH_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <Button
              onClick={reqAllStatuses}
              sx={{
                bgcolor: COLORS.panel2,
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                fontSize: 13,
                textTransform: 'none',
                '&:hover': { borderColor: COLORS.accent },
              }}
            >
              Atualizar agora
            </Button>
            <Button
              onClick={() => setIsDeviceSyncModalOpen(true)}
              sx={{
                bgcolor: COLORS.panel2,
                color: COLORS.accent,
                border: `1px solid ${COLORS.accent}`,
                borderRadius: '6px',
                fontSize: 13,
                textTransform: 'none',
                '&:hover': { bgcolor: 'var(--app-accent-bg)' },
              }}
            >
              Sincronizar dispositivos
            </Button>
          </Box>
        </Box>

        {!isLoading && sortedStatuses.length === 0 && (
          <Box sx={{ color: COLORS.muted, fontSize: 13, padding: '12px 0' }}>
            Nenhuma integração configurada.
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '16px',
          }}
        >
          {sortedStatuses.map((item) => (
            <IntegrationCard
              key={item.serviceName}
              item={item}
              onOpenDetails={setDetailsServiceName}
            />
          ))}
        </Box>
      </Box>

      <ProgressDetailDialog
        item={detailsItem}
        schedule={detailsSchedule}
        onClose={() => setDetailsServiceName(null)}
        onSaveSchedule={handleSaveSchedule}
        isSavingSchedule={isSavingSchedule}
        onTriggerNow={handleTriggerNow}
        isTriggering={isTriggering}
      />

      <DeviceFacialSyncModal
        open={isDeviceSyncModalOpen}
        onClose={() => setIsDeviceSyncModalOpen(false)}
        onJobStarted={(jobId) => {
          setDeviceSyncJobId(jobId);
          setIsDeviceSyncToastOpen(true);
        }}
        onError={(message) => showSnackbar(message, 'error')}
      />

      {deviceSyncJob && (
        <>
          <SyncActiveToast
            open={isDeviceSyncToastOpen}
            personName="Sincronização de dispositivos"
            progress={deviceSyncJob.progress}
            statusMessage={deviceSyncJob.message ?? undefined}
            success={deviceSyncJob.status === 'Completed'}
            successLingerMs={5000}
            operation="update"
            doneMessageOverride={deviceSyncJob.message ?? undefined}
            onView={() => {
              setIsDeviceSyncDialogOpen(true);
              setIsDeviceSyncToastOpen(false);
            }}
            onClose={() => setIsDeviceSyncToastOpen(false)}
          />
          <ProgressDialog
            open={isDeviceSyncDialogOpen}
            onClose={() => {
              setIsDeviceSyncDialogOpen(false);
              setDeviceSyncJobId(null);
            }}
            onMinimize={() => {
              setIsDeviceSyncDialogOpen(false);
              setIsDeviceSyncToastOpen(true);
            }}
            progress={deviceSyncJob.progress}
            status={
              deviceSyncJob.status === 'Completed'
                ? 'success'
                : deviceSyncJob.status === 'Failed'
                ? 'error'
                : deviceSyncJob.status === 'Canceled'
                ? 'canceled'
                : 'loading'
            }
            statusMessage={deviceSyncJob.message ?? ''}
            title="Sincronização de dispositivos"
          />
        </>
      )}
    </>
  );
}

export default SyncStatusPage;
